import { InfoOutlined } from "@mui/icons-material";
import { Box, Checkbox, Typography, useTheme } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { ContainerCard } from "../../../../components";
import { useContainerWidth } from "../../../../components/LineChart/hooks/useContainerWidth";
import { Tooltip } from "../../../../components/Tooltip";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useLocalization } from "../../../../hooks/useLocalization";
import { useTooltipAnimation } from "../../../../hooks/useTooltipAnimation";
import { useScrollbarDebounce } from "../hooks/useScrollbarDebounce";
import { useUsageChartData } from "../hooks/useUsageChartData";
import type {
  CustomBarShapeProps,
  CustomTickProps,
  CustomTooltipPayloadItem,
  UsagePerExperienceProps,
  UsageYAxisTickProps,
} from "../types";
import { exportUsagePerExperienceCsv } from "../utils/exportCsv";
import {
  ChartDatum,
  formatExperienceName,
  formatYAxisValueCompact,
  getDisplayValue,
  MetricType,
  MIN_DISPLAY_VALUE,
  ScaleMode,
} from "../utils/usageUtils";
import { DataAnalyticsChartSkeleton } from "./DataAnalyticsChartSkeleton";
import { LabeledSelectField } from "./LabeledSelectField";

const USAGE_CHART_Y_AXIS_WIDTH = 44;
const USAGE_FIXED_Y_AXIS_COLUMN_WIDTH = 44.5;

const getDomainMin = (
  isUserLevelDataEnabled: boolean,
  useLogScale: boolean,
): number => {
  if (!isUserLevelDataEnabled) return MIN_DISPLAY_VALUE;

  return useLogScale ? 1 : 0;
};

const USAGE_CHART_MARGIN = {
  top: 20,
  right: 2,
  left: 0,
  bottom: 10,
} as const;

const USAGE_BAR_MAX_SIZE = 24;
const USAGE_BAR_GAP = 4;
const USAGE_BAR_CATEGORY_GAP_RATIO = 0.2;
const USAGE_BAR_CATEGORY_GAP_RATIO_SM = 0.4;
const CATEGORY_WIDTH_PER_EXPERIENCE = 80;
const USAGE_X_AXIS_HEIGHT = 100;

const CustomXAxisTick: React.FC<CustomTickProps> = ({
  x = 0,
  y = 0,
  payload,
  fill,
}) => {
  const theme = useTheme();

  if (!payload?.value) return null;
  const lines = formatExperienceName(payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        fill={fill ?? theme.colors.text[1]}
        fontSize={10}
        textAnchor="end"
        transform="rotate(-50)"
      >
        {lines.map((line, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <tspan key={`line-${line}-${index}`} x={0} y={15 + index * 12}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const FixedUsageYAxis: React.FC<{
  height: number;
  yAxisDomainMax: number;
  yAxisTicks: number[];
  useLogScale: boolean;
  isUserLevelDataEnabled?: boolean;
}> = ({
  height,
  yAxisDomainMax,
  yAxisTicks,
  useLogScale,
  isUserLevelDataEnabled = false,
}) => {
  const theme = useTheme();
  const minDomain = getDomainMin(isUserLevelDataEnabled, useLogScale);

  return (
    <ResponsiveContainer height={height} width="100%">
      <BarChart
        data={[{ axis: "axis", value: 0 }]}
        margin={{
          top: USAGE_CHART_MARGIN.top,
          right: 0,
          left: 0,
          bottom: USAGE_CHART_MARGIN.bottom,
        }}
      >
        <XAxis
          axisLine={false}
          dataKey="axis"
          height={USAGE_X_AXIS_HEIGHT}
          interval={0}
          tick={false}
          tickLine={false}
        />
        <YAxis
          allowDataOverflow
          axisLine={{ stroke: theme.colors.secondary[3] }}
          // FIX: Force bottom of the chart to strictly be MIN_DISPLAY_VALUE
          domain={[minDomain, yAxisDomainMax]}
          interval={0}
          scale={useLogScale ? "log" : "auto"}
          tick={
            <UsageYAxisTick
              fillColor={theme.colors.text[3]}
              fontFamily={theme.typography.fontFamily}
              isUserLevelDataEnabled={isUserLevelDataEnabled}
              useLogScale={useLogScale}
            />
          }
          tickLine={false}
          ticks={yAxisTicks}
          type="number"
          width={USAGE_CHART_Y_AXIS_WIDTH}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

const CustomChartTooltip: React.FC<{
  active?: boolean;
  payload?: CustomTooltipPayloadItem[];
  label?: string;
  getMetricLabel: (m: MetricType) => string;
  isUserLevelDataEnabled?: boolean;
}> = ({
  active,
  payload,
  label,
  getMetricLabel,
  isUserLevelDataEnabled = false,
}) => {
  const theme = useTheme();
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const metricOrder = Object.values(MetricType);

    return metricOrder.indexOf(a.dataKey) - metricOrder.indexOf(b.dataKey);
  });

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        p: 1.5,
        borderRadius: "8px",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[3],
        maxWidth: 260,
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: 13,
          mb: 1,
          wordBreak: "break-word",
          color: theme.colors.text[6],
        }}
      >
        {sortedPayload[0]?.payload.name ?? label}
      </Typography>
      {sortedPayload.map((entry) => {
        const metric = entry.dataKey;
        const d = entry.payload;

        const rawKey = `raw_${metric}` as keyof ChartDatum;
        const raw = d[rawKey] as number;

        return (
          <Box
            key={`${metric}-${d.id}`}
            sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                backgroundColor: entry.color,
                flexShrink: 0,
              }}
            />
            <Typography sx={{ fontSize: 12, color: theme.colors.text[6] }}>
              {getMetricLabel(metric)}:{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {getDisplayValue(raw, metric, isUserLevelDataEnabled)}
              </Box>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const UsageYAxisTick: React.FC<
  UsageYAxisTickProps & { isUserLevelDataEnabled?: boolean }
> = ({
  x = 0,
  y = 0,
  payload,
  fillColor,
  fontFamily,
  isUserLevelDataEnabled = false,
  useLogScale = false,
}) => {
  const value = payload?.value ?? 0;
  // Log scale cannot represent 0, so the axis floor is 1 when log scale is active
  const minValue = getDomainMin(isUserLevelDataEnabled, useLogScale);
  const isMin = Math.abs(value - minValue) < 1e-6;

  const minLabel = isUserLevelDataEnabled
    ? String(minValue)
    : `<${MIN_DISPLAY_VALUE}`;
  const label = isMin ? minLabel : formatYAxisValueCompact(value);

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        dominantBaseline="middle"
        fill={fillColor}
        fontFamily={fontFamily}
        fontSize={10}
        textAnchor="end"
        x={-4}
        y={0}
      >
        {label}
      </text>
    </g>
  );
};

const CustomBarShape: React.FC<CustomBarShapeProps> = (props) => {
  const { x, y, width, height, payload, fillKey, defaultFill } = props;
  const fill = (payload?.[fillKey] ?? defaultFill) as string;

  return <rect fill={fill} height={height} width={width} x={x} y={y} />;
};

const XAxisTickWithMap: React.FC<
  CustomTickProps & { chartIdToName?: Record<string, string> }
> = ({ chartIdToName = {}, fill, payload, x, y }) => (
  <CustomXAxisTick
    fill={fill}
    payload={{
      value: chartIdToName[payload?.value ?? ""] ?? payload?.value ?? "",
    }}
    x={x}
    y={y}
  />
);

// --- Main Component ---

export const UsagePerExperience: React.FC<UsagePerExperienceProps> = ({
  data,
  isLoading = false,
  isFetching = false,
  showCertifications = false,
  isUserLevelDataEnabled = false,
  startDate,
  endDate,
}) => {
  const theme = useTheme();
  const { t } = useLocalization();
  const isMobile = useIsMobile();

  const handleExportCsv = useCallback(() => {
    exportUsagePerExperienceCsv(
      data,
      isUserLevelDataEnabled,
      {
        experienceName: t(
          "pages.dataAnalytics.internalUsage.exportColumns.experienceName",
        ),
        hoursInVr: t(
          "pages.dataAnalytics.internalUsage.exportColumns.hoursInVr",
        ),
        userSessions: t(
          "pages.dataAnalytics.internalUsage.exportColumns.userSessions",
        ),
        uniqueActiveUsers: t(
          "pages.dataAnalytics.internalUsage.exportColumns.uniqueActiveUsers",
        ),
        certifications: t(
          "pages.dataAnalytics.internalUsage.exportColumns.certifications",
        ),
      },
      "usage-per-experience.csv",
      { startDate, endDate },
      {
        startDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeStart",
        ),
        endDate: t(
          "pages.dataAnalytics.internalUsage.exportColumns.dateRangeEnd",
        ),
      },
      showCertifications,
    );
  }, [
    data,
    isUserLevelDataEnabled,
    t,
    startDate,
    endDate,
    showCertifications,
  ]);

  const {
    ref: containerRef,
    chartHeight,
    width: containerWidth,
  } = useContainerWidth<HTMLDivElement>(isMobile);

  useTooltipAnimation(containerRef);
  const compensatedChartHeight = (chartHeight || 320) + 70;

  const scrollbarSx = useScrollbarDebounce(containerWidth);

  const availableMetrics = useMemo(() => {
    const ordered = [
      MetricType.USER_SESSIONS,
      MetricType.HOURS_IN_VR,
      MetricType.UNIQUE_ACTIVE_USERS,
      MetricType.CERTIFICATIONS,
    ];

    return showCertifications
      ? ordered
      : ordered.filter((m) => m !== MetricType.CERTIFICATIONS);
  }, [showCertifications]);

  const metricColors: Record<MetricType, string> = useMemo(
    () => ({
      [MetricType.HOURS_IN_VR]: theme.colors.metrics.hoursInVr,
      [MetricType.USER_SESSIONS]: theme.colors.metrics.userSessions,
      [MetricType.UNIQUE_ACTIVE_USERS]: theme.colors.metrics.uniqueActiveUsers,
      [MetricType.CERTIFICATIONS]: theme.colors.metrics.certifications,
    }),
    [theme.colors.metrics],
  );

  const [selectedMetrics, setSelectedMetrics] =
    useState<MetricType[]>(availableMetrics);
  const [sortByMetric, setSortByMetric] = useState<MetricType>(
    MetricType.USER_SESSIONS,
  );
  const [scaleMode, setScaleMode] = useState<ScaleMode>("linear");

  const handleMetricChange = (metric: MetricType) => {
    setSelectedMetrics((prev) => {
      const next = prev.includes(metric)
        ? prev.filter((m) => m !== metric)
        : [...prev, metric];

      next.sort(
        (a, b) => availableMetrics.indexOf(a) - availableMetrics.indexOf(b),
      );

      const nextMetric = next[0];
      if (!next.includes(sortByMetric) && nextMetric) {
        setSortByMetric(nextMetric);
      }

      return next;
    });
  };

  const metricLabels: Record<MetricType, string> = useMemo(
    () => ({
      [MetricType.HOURS_IN_VR]: t(
        "pages.dataAnalytics.internalUsage.usagePerExperience.metrics.hoursInVr",
      ),
      [MetricType.USER_SESSIONS]: t(
        "pages.dataAnalytics.internalUsage.usagePerExperience.metrics.userSessions",
      ),
      [MetricType.UNIQUE_ACTIVE_USERS]: t(
        "pages.dataAnalytics.internalUsage.usagePerExperience.metrics.uniqueActiveUsers",
      ),
      [MetricType.CERTIFICATIONS]: t(
        "pages.dataAnalytics.internalUsage.usagePerExperience.metrics.certifications",
      ),
    }),
    [t],
  );

  const metricTooltips: Record<MetricType, string> = useMemo(
    () => ({
      [MetricType.HOURS_IN_VR]: t(
        "pages.dataAnalytics.internalUsage.usagePerExperience.tooltips.hoursInVr",
      ),
      [MetricType.USER_SESSIONS]: t(
        "pages.dataAnalytics.internalUsage.usagePerExperience.tooltips.userSessions",
      ),
      [MetricType.UNIQUE_ACTIVE_USERS]: t(
        "pages.dataAnalytics.internalUsage.usagePerExperience.tooltips.uniqueActiveUsers",
      ),
      [MetricType.CERTIFICATIONS]: t(
        "pages.dataAnalytics.internalUsage.usagePerExperience.tooltips.certifications",
      ),
    }),
    [t],
  );

  const getMetricLabel = useCallback(
    (metric: MetricType) => metricLabels[metric],
    [metricLabels],
  );
  const getMetricTooltip = useCallback(
    (metric: MetricType) => metricTooltips[metric],
    [metricTooltips],
  );

  const { chartData, logJustified, yAxisDomainMax, yAxisTicks, useLogScale } =
    useUsageChartData({
      data,
      availableMetrics,
      selectedMetrics,
      sortByMetric,
      scaleMode,
      setScaleMode,
      metricColors,
      isUserLevelDataEnabled,
    });

  const chartWidth = useMemo(() => {
    if (chartData.length === 0) return 0;

    return chartData.length * CATEGORY_WIDTH_PER_EXPERIENCE;
  }, [chartData.length]);

  const chartIdToName = useMemo(
    () =>
      Object.fromEntries(chartData.map((d) => [d.id, d.name])) as Record<
        string,
        string
      >,
    [chartData],
  );

  if (isMobile) {
    return null;
  }

  const renderMetricCheckboxes = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        justifyContent: "space-between",
      }}
    >
      {availableMetrics.map((metric) => (
        <Box
          key={metric}
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            gap: 1,
            justifyContent: { xs: "space-between", md: "flex-start" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Checkbox
              checked={selectedMetrics.includes(metric)}
              onChange={() => handleMetricChange(metric)}
              sx={{
                color: metricColors[metric],
                height: 20,
                width: 20,
                borderRadius: "4px",
                mr: "6px",
                "&.Mui-checked": { color: metricColors[metric] },
              }}
            />
            <Typography
              sx={{
                fontSize: 14,
                color: theme.colors.text[6],
                fontWeight: 500,
              }}
            >
              {getMetricLabel(metric)}
            </Typography>
          </Box>
          <Tooltip message={getMetricTooltip(metric)} placement="right">
            <InfoOutlined
              sx={{ fontSize: 20, color: theme.colors.secondary[6] }}
            />
          </Tooltip>
        </Box>
      ))}
    </Box>
  );

  const headerAction =
    selectedMetrics.length > 0 ? (
      <Box
        sx={{
          display: "flex",
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "flex-end" },
          flexWrap: "wrap",
        }}
      >
        {/* Sort by */}
        <LabeledSelectField<MetricType>
          containerHeight={60}
          containerWidth={225}
          label={t(
            "pages.dataAnalytics.internalUsage.usagePerExperience.sortBy",
          )}
          onChange={setSortByMetric}
          options={selectedMetrics.map((metric) => ({
            value: metric,
            label: getMetricLabel(metric),
          }))}
          selectWidth={225}
          value={sortByMetric}
        />

        {/* Scale */}
        {logJustified && (
          <LabeledSelectField<ScaleMode>
            label={t(
              "pages.dataAnalytics.internalUsage.usagePerExperience.scale",
            )}
            labelAdornment={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: "999px",
                    backgroundColor: theme.colors.secondary[0],
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: theme.colors.text[5],
                    }}
                  >
                    {useLogScale ? "Log scale" : "Log recommended"}
                  </Typography>
                </Box>
                <Tooltip
                  message={
                    useLogScale
                      ? "Log scale makes large ranges easier to compare."
                      : "Large ranges detected. Switch to Log."
                  }
                  placement="left"
                >
                  <InfoOutlined
                    sx={{ fontSize: 18, color: theme.colors.secondary[6] }}
                  />
                </Tooltip>
              </Box>
            }
            onChange={setScaleMode}
            options={[
              { value: "linear", label: "Linear" },
              { value: "log", label: "Log" },
            ]}
            selectWidth={160}
            value={scaleMode}
          />
        )}
      </Box>
    ) : undefined;

  return (
    <ContainerCard
      action={headerAction}
      exportAction={{
        label: t("pages.dataAnalytics.internalUsage.exportCsv"),
        onClick: handleExportCsv,
      }}
      isFetching={isFetching && !isLoading}
      subtitle={t(
        "pages.dataAnalytics.internalUsage.usagePerExperience.description",
      )}
      sx={{ paddingRight: 5 }}
      title={t("pages.dataAnalytics.internalUsage.usagePerExperience.title")}
    >
      {selectedMetrics.length === 0 ? (
        <>
          <Box sx={{ mb: 3, pl: { xs: "8px", md: "60px" }, pr: "20px" }}>
            {renderMetricCheckboxes()}
          </Box>
          <Box
            sx={{
              p: 4,
              backgroundColor: theme.colors.secondary[0],
              borderRadius: "16px",
              textAlign: "center",
            }}
          >
            <Typography sx={{ color: theme.colors.text[1], fontSize: 14 }}>
              {t(
                "pages.dataAnalytics.internalUsage.usagePerExperience.noMetricsSelected",
              )}
            </Typography>
          </Box>
        </>
      ) : (
        <>
          {isLoading && (
            <Box sx={{ pt: 1 }}>
              <DataAnalyticsChartSkeleton
                chartHeight={Math.max(compensatedChartHeight, 320)}
                footerRowHeight={40}
                showFooterRow={true}
                showTopControls={false}
              />
            </Box>
          )}
          {!isLoading && (
            <Box ref={containerRef} sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: `${USAGE_FIXED_Y_AXIS_COLUMN_WIDTH}px minmax(0, 1fr)`,
                  alignItems: "start",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    width: USAGE_FIXED_Y_AXIS_COLUMN_WIDTH,
                    height: compensatedChartHeight,
                    minHeight: compensatedChartHeight,
                    flexShrink: 0,
                    overflow: "hidden",
                    "& .recharts-wrapper *:focus": { outline: "none" },
                  }}
                >
                  <FixedUsageYAxis
                    height={compensatedChartHeight}
                    isUserLevelDataEnabled={isUserLevelDataEnabled}
                    useLogScale={useLogScale}
                    yAxisDomainMax={yAxisDomainMax}
                    yAxisTicks={yAxisTicks}
                  />
                </Box>
                <Box
                  sx={{
                    overflowX: "scroll",
                    pb: 2,
                    minWidth: 0,
                    ...scrollbarSx,
                  }}
                >
                  <Box
                    sx={{
                      width: chartWidth || "100%",
                      minWidth: "100%",
                      height: compensatedChartHeight,
                      minHeight: compensatedChartHeight,
                      position: "relative",
                      "& .recharts-wrapper *:focus": { outline: "none" },
                    }}
                  >
                    <ResponsiveContainer
                      height={compensatedChartHeight}
                      width="100%"
                    >
                      <BarChart
                        barCategoryGap={`${
                          (chartData.length < 4
                            ? USAGE_BAR_CATEGORY_GAP_RATIO_SM
                            : USAGE_BAR_CATEGORY_GAP_RATIO) * 100
                        }%`}
                        barGap={USAGE_BAR_GAP}
                        data={chartData}
                        margin={USAGE_CHART_MARGIN}
                        maxBarSize={USAGE_BAR_MAX_SIZE}
                      >
                        <CartesianGrid
                          stroke={theme.colors.secondary[1]}
                          vertical={true}
                        />

                        <XAxis
                          axisLine={{ stroke: theme.colors.secondary[3] }}
                          dataKey="id"
                          height={USAGE_X_AXIS_HEIGHT}
                          interval={0}
                          padding={{ left: 40, right: 4 }}
                          tick={
                            <XAxisTickWithMap
                              chartIdToName={chartIdToName}
                              fill={theme.colors.text[3]}
                            />
                          }
                          tickLine={false}
                          tickMargin={8}
                        />
                        <YAxis
                          domain={[
                            getDomainMin(isUserLevelDataEnabled, useLogScale),
                            yAxisDomainMax,
                          ]}
                          hide={true}
                          interval={0}
                          scale={useLogScale ? "log" : "auto"}
                          ticks={yAxisTicks}
                          type="number"
                          width={0}
                        />

                        <RechartsTooltip
                          content={
                            <CustomChartTooltip
                              getMetricLabel={getMetricLabel}
                              isUserLevelDataEnabled={isUserLevelDataEnabled}
                            />
                          }
                          cursor={{ fill: theme.palette.action.hover }}
                        />

                        {availableMetrics.map((metric) => {
                          const isSelected = selectedMetrics.includes(metric);
                          const fillKey =
                            `fill${metric.charAt(0).toUpperCase() + metric.slice(1)}` as keyof ChartDatum;

                          return (
                            <Bar
                              dataKey={metric}
                              fill={metricColors[metric]}
                              hide={!isSelected}
                              key={metric}
                              minPointSize={4}
                              name={metric}
                              shape={
                                <CustomBarShape
                                  defaultFill={metricColors[metric]}
                                  fillKey={fillKey}
                                />
                              }
                            />
                          );
                        })}
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </Box>
            </Box>
          )}

          {!isLoading && (
            <Box sx={{ mt: 2, pl: { xs: "8px", md: "60px" }, pr: "20px" }}>
              {renderMetricCheckboxes()}
            </Box>
          )}
        </>
      )}
    </ContainerCard>
  );
};
